import { Component, effect, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { Vehicle } from 'src/app/models/vehicle.model';
import { CoreService } from 'src/app/services/core/core.service';
import { InspectionService } from 'src/app/services/pre-use-inspection/inspection.service';
import { VehicleProvider } from 'src/app/services/providers/vehicle-provider/vehicle-provider';

@Component({
  selector: 'app-vehicle-detail-card',
  templateUrl: './vehicle-detail-card.component.html',
  styleUrls: ['./vehicle-detail-card.component.scss'],
  standalone: false,
})
export class VehicleDetailCardComponent {
  /* Vehicle data rendered inside the card */
  vehicle = input<Vehicle>();

  /* Index of the card within the parent list */
  currentIndex = input<number>();

  /* Flags controlling UI actions and visibility */
  isInspect = input<boolean>();
  isMobileApp = input<boolean>();
  showButtons = input<boolean>();
  showNotMyVehicle = input<boolean>();

  /* Currently selected vehicle index */
  selectedIndex = 0;

  /* Emits navigation intent with selected vehicle */
  navigationVehicle = output<Vehicle>();

  constructor(
    private readonly router: Router,
    private readonly coreService: CoreService,
    private readonly inspectionService: InspectionService,
    private readonly vehicleProvider: VehicleProvider
  ) {
    effect(() => {
      this.selectedIndex = this.vehicleProvider.readSelectedVehicleIdx();
    });
  }

  /* Resolves vehicle image based on vehicle type */
  getVehicleImage(): string {
    const car = this.vehicle();

    if (!car) {
      return 'assets/imgs/no-asset.png';
    }

    switch (car.vehiclE_IMAGE_TYPE?.toLowerCase()) {
      case 'van':
        return 'assets/imgs/van.png';
      case 'truck':
        return 'assets/imgs/truck.png';
      case 'car':
      default:
        return 'assets/imgs/no-asset.png';
    }
  }

  /* Marks the current vehicle as selected */
  selectVehicle(): void {
    const selectedVehicle = this.vehicle();
    const index = this.currentIndex();

    if (!selectedVehicle || index === undefined) {
      return;
    }

    this.vehicleProvider.setSelectedVehicle(selectedVehicle, index);
  }

  /* Emits navigation event for vehicle details */
  navigateVehicleDetails(): void {
    const vehicleDetails = this.vehicle();
    const index = this.currentIndex();

    if (!vehicleDetails) {
      return;
    }

    if (index != undefined && index >= 0) {
      this.vehicleProvider.setSelectedVehicle(vehicleDetails, index);
    }

    this.navigationVehicle.emit(vehicleDetails);
  }

  /* Navigates to not-my-vehicle flow with vehicle context */
  notMyVehicle(): void {
    this.router.navigate(['not-my-vehicle', this.vehicle()?.icN_NO], {
      state: {
        vehicleDetails: this.vehicle(),
      },
    });
  }

  /* Redirects to notification screen */
  goToNotification(vehicle: Vehicle | undefined) {
    this.router.navigate(['/notifications', vehicle?.icN_NO, true]);
  }

  /* Navigates to PUI flow */
  async navigatePui(): Promise<void> {
    await this.inspectionService.navigatePUI(this.vehicle()?.icN_NO.toString() ?? '');
  }

  /* Gets the feature flag value for open defects screen */
  get isFeatureShowOpenDefectScreen() {
    return this.coreService.isFeatureFlagExists('FeatureShowOpenDefectScreen');
  }

  get showNotification() {
    return this.coreService.isFeatureFlagExists('Notification');
  }
}
